// Aba "Agentes em campo" do Férias e Licenças (redesenho 27/10/2026).
// Calendário do mês: quantos agentes de campo ficam em cada dia útil.
// Ignora os filtros da tela. Faltas e saídas de turno não entram;
// não-oficializados entram (regra em afastamentosDatas.ts).

import { useMemo, useState } from 'react';
import { Box, Paper, Typography, IconButton, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  RegistroAfast, resumoMes, foraNoDia, dataRetorno, rotuloTipo, diaDaSemana, diaUtil,
  diasNoMes, montarIso, MESES, SEMANA_LONGA, fmtLonga, fmtSemLonga, fmtDM,
} from './afastamentosDatas';
import { coresDia, CINZA_DIA, CARTAO_SX, AZUL_ABA, VERDE_ESCURO } from './visual';

interface Props {
  todos: RegistroAfast[];
  hoje: string;
  total: number | null;
  idsEquipe?: number[];
}

const CABECALHO = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function primeiroDiaUtil(ano: number, mes: number): string {
  for (let d = 1; d <= diasNoMes(ano, mes); d++) {
    const iso = montarIso(ano, mes, d);
    if (diaUtil(iso)) return iso;
  }
  return montarIso(ano, mes, 1);
}

export default function CalendarioEquipe({ todos, hoje, total, idsEquipe }: Props) {
  const [anoHoje, mesHoje] = hoje.split('-').map(Number);
  const [ano, setAno] = useState(anoHoje);
  const [mes, setMes] = useState(mesHoje);
  const [diaSel, setDiaSel] = useState(hoje);

  const n = total ?? 0;
  const resumo = useMemo(() => resumoMes(todos, ano, mes, n, idsEquipe), [todos, ano, mes, n, idsEquipe]);

  const irPara = (a: number, m: number) => {
    setAno(a);
    setMes(m);
    setDiaSel(a === anoHoje && m === mesHoje ? hoje : primeiroDiaUtil(a, m));
  };
  const anterior = () => (mes === 1 ? irPara(ano - 1, 12) : irPara(ano, mes - 1));
  const proximo = () => (mes === 12 ? irPara(ano + 1, 1) : irPara(ano, mes + 1));
  const ehMesDeHoje = ano === anoHoje && mes === mesHoje;

  const deslocamento = (diaDaSemana(montarIso(ano, mes, 1)) + 6) % 7;   // grade começa na segunda
  const foraSel = foraNoDia(todos, diaSel, idsEquipe);

  // "Menor equipe no mês"
  let menorTxt = '—';
  let menorSub = '';
  if (total !== null && resumo.menor !== null) {
    if (resumo.menor >= total) {
      menorTxt = `${total} de ${total} em campo`;
      menorSub = 'Equipe completa o mês todo';
    } else {
      menorTxt = `${resumo.menor} de ${total} em campo`;
      const de = resumo.menorDe as string;
      const ate = resumo.menorAte as string;
      menorSub = de === ate ? `em ${fmtDM(de)}` : `entre ${de.slice(8)} e ${fmtDM(ate)}`;
    }
  }

  const textoEmCampo = (fora: number) => (total !== null ? `${Math.max(0, total - fora)} de ${total}` : `${fora} fora`);

  return (
    <Box>
      {/* Cabeçalho: mês + menor equipe */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch', mb: 2 }}>
        <Paper elevation={0} sx={{ ...CARTAO_SX, p: 1.5, flex: '2 1 360px', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <IconButton aria-label="Mês anterior" onClick={anterior}
            sx={{ width: 52, height: 52, border: '2px solid #D1D5DB' }}>
            <ChevronLeftIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#111827', flex: '1 1 160px', textAlign: 'center' }}>
            {MESES[mes - 1]} {ano}
          </Typography>
          <IconButton aria-label="Próximo mês" onClick={proximo}
            sx={{ width: 52, height: 52, border: '2px solid #D1D5DB' }}>
            <ChevronRightIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <Button variant="outlined" onClick={() => irPara(anoHoje, mesHoje)} disabled={ehMesDeHoje && diaSel === hoje}
            sx={{ minHeight: 44, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
            Voltar para hoje
          </Button>
        </Paper>
        <Paper elevation={0} sx={{ ...CARTAO_SX, p: 2, flex: '1 1 220px' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.6, color: '#6B7280' }}>MENOR EQUIPE NO MÊS</Typography>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#111827', mt: 0.5 }}>{menorTxt}</Typography>
          <Typography sx={{ fontSize: 14, color: '#4B5563' }}>{menorSub}</Typography>
        </Paper>
      </Box>

      {/* Legenda */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1.5, fontSize: 14, color: '#374151' }}>
        {[
          { c: coresDia(0), t: 'Equipe completa' },
          { c: coresDia(1), t: '1 a 3 fora' },
          { c: coresDia(4), t: '4 ou mais fora' },
          { c: { bg: CINZA_DIA.bg, borda: '#9CA3AF' }, t: 'Fim de semana / feriado' },
        ].map(l => (
          <Box key={l.t} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box sx={{ width: 18, height: 18, borderRadius: 1, backgroundColor: l.c.bg, border: `2px solid ${l.c.borda}` }} />
            {l.t}
          </Box>
        ))}
        <Box sx={{ color: '#6B7280', fontStyle: 'italic' }}>Clique num dia para ver quem está fora.</Box>
      </Box>

      {/* Grade seg → dom */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 1 }, mb: 3 }}>
        {CABECALHO.map(c => (
          <Box key={c} sx={{ textAlign: 'center', fontWeight: 700, fontSize: 13, color: '#6B7280', py: 0.5 }}>{c}</Box>
        ))}
        {Array.from({ length: deslocamento }).map((_, i) => <Box key={`v${i}`} />)}
        {resumo.dias.map(d => {
          const ehHoje = d.iso === hoje;
          const sel = d.iso === diaSel;
          const seloHoje = ehHoje && (
            <Box component="span" sx={{ position: 'absolute', top: 4, right: 4, fontSize: 10, fontWeight: 800,
              backgroundColor: '#111827', color: 'white', px: 0.6, borderRadius: 1 }}>HOJE</Box>
          );
          if (!d.util) {
            const nome = d.feriado || SEMANA_LONGA[diaDaSemana(d.iso)];
            return (
              <Box key={d.iso} sx={{
                position: 'relative', minHeight: { xs: 64, sm: 92 }, borderRadius: 2, p: { xs: 0.5, sm: 1 },
                backgroundColor: CINZA_DIA.bg, border: `1px solid ${CINZA_DIA.borda}`, color: CINZA_DIA.texto, overflow: 'hidden',
              }}>
                {seloHoje}
                <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }}>{d.dia}</Typography>
                <Typography sx={{ fontSize: { xs: 9, sm: 12 }, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{nome}</Typography>
              </Box>
            );
          }
          const cor = coresDia(d.fora.length);
          return (
            <Box
              key={d.iso}
              component="button"
              type="button"
              onClick={() => setDiaSel(d.iso)}
              aria-label={`${fmtLonga(d.iso)}: ${textoEmCampo(d.fora.length)} em campo`}
              aria-pressed={sel}
              sx={{
                position: 'relative', minHeight: { xs: 64, sm: 92 }, borderRadius: 2, p: { xs: 0.5, sm: 1 },
                textAlign: 'left', cursor: 'pointer', font: 'inherit', overflow: 'hidden',
                backgroundColor: cor.bg, color: cor.texto,
                border: `2px solid ${cor.borda}`,
                outline: sel ? `3px solid ${AZUL_ABA}` : 'none', outlineOffset: 2,
                '&:hover': { filter: 'brightness(0.97)' },
              }}
            >
              {seloHoje}
              <Typography sx={{ fontSize: { xs: 13, sm: 15 }, fontWeight: 700 }}>{d.dia}</Typography>
              <Typography sx={{ fontSize: { xs: 13, sm: 22, md: 30 }, fontWeight: 800, lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                {textoEmCampo(d.fora.length)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Quadro do dia escolhido */}
      <Paper elevation={0} sx={{ ...CARTAO_SX, p: 2.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{fmtSemLonga(diaSel)}</Typography>
        <Typography sx={{ fontSize: 16, color: '#374151', mb: 1.5 }}>
          {total !== null ? `${Math.max(0, total - foraSel.length)} de ${total} em campo · ` : ''}
          {foraSel.length} fora
        </Typography>
        {foraSel.length === 0 ? (
          <Typography sx={{ fontSize: 16, color: VERDE_ESCURO, fontWeight: 600 }}>Ninguém afastado neste dia.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {foraSel.map(r => (
              <Box key={r.id} sx={{ flex: '1 1 220px', p: 1.5, borderRadius: 2, border: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>{r.agente_nome}</Typography>
                <Typography sx={{ fontSize: 14, color: '#4B5563' }}>{rotuloTipo(r.tipo)}</Typography>
                <Typography sx={{ fontSize: 14, color: VERDE_ESCURO, fontWeight: 700 }}>volta {fmtLonga(dataRetorno(r))}</Typography>
                {r.oficializado === false && (
                  <Typography sx={{ fontSize: 13, color: '#6B7280', fontStyle: 'italic' }}>não oficializado</Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
